// src/module_pharmacy/AddTransaction.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faSave, faTimes, faTruck, faUsers, faUserTie,
    faWallet, faDollarSign, faCalendarAlt, faCreditCard,
    faFileInvoice, faStickyNote, faReceipt, faLayerGroup,
    faChevronDown, faExchangeAlt, faCheckCircle, faExclamationTriangle,
    faSpinner, faTag, faStore, faSearch, faUser, faBuilding,
    faList, faFileAlt, faMoneyBillWave, faClock, faArrowRight,
    faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import "../module_mechanics/Styles/Trans.css";

// ==============================================
// API ENDPOINTS
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const TRANSACTIONS_API = `${API_BASE}/cosmetics_transactions.php`;
const CUSTOMERS_API = `${API_BASE}/mechanics_customers.php`;
const SUPPLIERS_API = `${API_BASE}/mechanics_suppliers.php`;
const EMPLOYEES_API = `${API_BASE}/mechanics_employees.php`;

// ==============================================
// HELPER FUNCTIONS
// ==============================================
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// ==============================================
// TRANSLATION SYSTEM
// ==============================================
const translations = {
    'en': {
        addTransaction: 'Add Transaction',
        addNewTransaction: 'Record a new financial transaction',
        customerDue: 'Customer Due',
        customerPayment: 'Customer Payment',
        supplierDue: 'Supplier Due',
        supplierPayment: 'Supplier Payment',
        salaryPayment: 'Salary Payment',
        shopExpense: 'Shop Expense',
        selectCustomer: 'Select Customer',
        selectSupplier: 'Select Supplier',
        selectEmployee: 'Select Employee',
        amount: 'Amount',
        date: 'Date',
        description: 'Description',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        creditCard: 'Credit Card',
        mobilePayment: 'Mobile Payment',
        invoiceNumber: 'Invoice Number',
        optional: 'Optional',
        save: 'Save',
        clear: 'Clear',
        saving: 'Saving...',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        transactionSaved: 'Transaction saved successfully!',
        failedSave: 'Failed to save transaction',
        selectShopFirst: 'Please select a shop first',
        noShopSelected: 'No shop selected',
        expenseCategory: 'Expense Category',
        expenseName: 'Expense Name',
        unpaidInvoices: 'Unpaid Invoices',
        noUnpaidInvoices: 'No unpaid invoices found',
        grandTotal: 'Grand Total',
        alreadyPaid: 'Already Paid',
        balanceDue: 'Balance Due',
        paymentType: 'Payment Type',
        fullPayment: 'Full Payment',
        partialPayment: 'Partial Payment',
        paymentDetails: 'Payment Details',
        notes: 'Notes',
        selectReference: 'Select Reference',
        expenseDetails: 'Expense Details',
        general: 'General',
        searchPlaceholder: 'Search...',
        noResults: 'No results found',
        select: 'Select',
        invoice: 'Invoice',
        due: 'Due',
        paid: 'Paid',
        total: 'Total',
        customer: 'Customer',
        supplier: 'Supplier',
        employee: 'Employee',
        name: 'Name',
        phone: 'Phone',
        email: 'Email',
        reference: 'Reference',
        selectInvoice: 'Select Invoice',
        paymentAmount: 'Payment Amount'
    },
    'ur': {
        addTransaction: 'ٹرانزیکشن شامل کریں',
        addNewTransaction: 'نیا مالی ٹرانزیکشن ریکارڈ کریں',
        customerDue: 'کسٹمر بقایا',
        customerPayment: 'کسٹمر ادائیگی',
        supplierDue: 'سپلائر بقایا',
        supplierPayment: 'سپلائر ادائیگی',
        salaryPayment: 'تنخواہ کی ادائیگی',
        shopExpense: 'دکان کا خرچ',
        selectCustomer: 'کسٹمر منتخب کریں',
        selectSupplier: 'سپلائر منتخب کریں',
        selectEmployee: 'ملازم منتخب کریں',
        amount: 'رقم',
        date: 'تاریخ',
        description: 'تفصیل',
        paymentMethod: 'ادائیگی کا طریقہ',
        cash: 'نقد',
        bankTransfer: 'بینک ٹرانسفر',
        cheque: 'چیک',
        creditCard: 'کریڈٹ کارڈ',
        mobilePayment: 'موبائل پیمنٹ',
        invoiceNumber: 'انوائس نمبر',
        optional: 'اختیاری',
        save: 'محفوظ کریں',
        clear: 'صاف کریں',
        saving: 'محفوظ کر رہے ہیں...',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        transactionSaved: 'ٹرانزیکشن کامیابی سے محفوظ ہو گئی!',
        failedSave: 'ٹرانزیکشن محفوظ کرنے میں ناکامی',
        selectShopFirst: 'پہلے ایک شاپ منتخب کریں',
        noShopSelected: 'کوئی شاپ منتخب نہیں',
        expenseCategory: 'خرچ کا زمرہ',
        expenseName: 'خرچ کا نام',
        unpaidInvoices: 'غیر ادا شدہ انوائسز',
        noUnpaidInvoices: 'کوئی غیر ادا شدہ انوائس نہیں',
        grandTotal: 'کل رقم',
        alreadyPaid: 'ادا شدہ رقم',
        balanceDue: 'بقایا رقم',
        paymentType: 'ادائیگی کی قسم',
        fullPayment: 'مکمل ادائیگی',
        partialPayment: 'جزوی ادائیگی',
        paymentDetails: 'ادائیگی کی تفصیلات',
        notes: 'نوٹس',
        selectReference: 'حوالہ منتخب کریں',
        expenseDetails: 'خرچ کی تفصیلات',
        general: 'عام',
        searchPlaceholder: 'تلاش کریں...',
        noResults: 'کوئی نتیجہ نہیں',
        select: 'منتخب کریں',
        invoice: 'انوائس',
        due: 'بقایا',
        paid: 'ادا شدہ',
        total: 'کل',
        customer: 'کسٹمر',
        supplier: 'سپلائر',
        employee: 'ملازم',
        name: 'نام',
        phone: 'فون',
        email: 'ای میل',
        reference: 'حوالہ',
        selectInvoice: 'انوائس منتخب کریں',
        paymentAmount: 'ادائیگی کی رقم'
    },
    'ar': {
        addTransaction: 'إضافة معاملة',
        addNewTransaction: 'تسجيل معاملة مالية جديدة',
        customerDue: 'مستحقات العميل',
        customerPayment: 'دفع العميل',
        supplierDue: 'مستحقات المورد',
        supplierPayment: 'دفع للمورد',
        salaryPayment: 'دفع الراتب',
        shopExpense: 'مصروف المحل',
        selectCustomer: 'اختر العميل',
        selectSupplier: 'اختر المورد',
        selectEmployee: 'اختر الموظف',
        amount: 'المبلغ',
        date: 'التاريخ',
        description: 'الوصف',
        paymentMethod: 'طريقة الدفع',
        cash: 'نقد',
        bankTransfer: 'تحويل بنكي',
        cheque: 'شيك',
        creditCard: 'بطاقة ائتمان',
        mobilePayment: 'دفع عبر الجوال',
        invoiceNumber: 'رقم الفاتورة',
        optional: 'اختياري',
        save: 'حفظ',
        clear: 'مسح',
        saving: 'جاري الحفظ...',
        success: 'نجاح',
        error: 'خطأ',
        warning: 'تحذير',
        transactionSaved: 'تم حفظ المعاملة بنجاح!',
        failedSave: 'فشل في حفظ المعاملة',
        selectShopFirst: 'الرجاء اختيار متجر أولاً',
        noShopSelected: 'لم يتم اختيار متجر',
        expenseCategory: 'فئة المصروف',
        expenseName: 'اسم المصروف',
        unpaidInvoices: 'فواتير غير مدفوعة',
        noUnpaidInvoices: 'لا توجد فواتير غير مدفوعة',
        grandTotal: 'المجموع الكلي',
        alreadyPaid: 'المدفوع',
        balanceDue: 'الرصيد المستحق',
        paymentType: 'نوع الدفع',
        fullPayment: 'دفع كامل',
        partialPayment: 'دفع جزئي',
        paymentDetails: 'تفاصيل الدفع',
        notes: 'ملاحظات',
        selectReference: 'اختر المرجع',
        expenseDetails: 'تفاصيل المصروف',
        general: 'عام',
        searchPlaceholder: 'بحث...',
        noResults: 'لا توجد نتائج',
        select: 'اختر',
        invoice: 'فاتورة',
        due: 'مستحق',
        paid: 'مدفوع',
        total: 'المجموع',
        customer: 'عميل',
        supplier: 'مورد',
        employee: 'موظف',
        name: 'الاسم',
        phone: 'الهاتف',
        email: 'البريد الإلكتروني',
        reference: 'المرجع',
        selectInvoice: 'اختر فاتورة',
        paymentAmount: 'مبلغ الدفع'
    },
    'es': {
        addTransaction: 'Agregar Transacción',
        addNewTransaction: 'Registrar una nueva transacción financiera',
        customerDue: 'Deuda del Cliente',
        customerPayment: 'Pago del Cliente',
        supplierDue: 'Deuda del Proveedor',
        supplierPayment: 'Pago al Proveedor',
        salaryPayment: 'Pago de Salario',
        shopExpense: 'Gasto de Tienda',
        selectCustomer: 'Seleccionar Cliente',
        selectSupplier: 'Seleccionar Proveedor',
        selectEmployee: 'Seleccionar Empleado',
        amount: 'Monto',
        date: 'Fecha',
        description: 'Descripción',
        paymentMethod: 'Método de Pago',
        cash: 'Efectivo',
        bankTransfer: 'Transferencia Bancaria',
        cheque: 'Cheque',
        creditCard: 'Tarjeta de Crédito',
        mobilePayment: 'Pago Móvil',
        invoiceNumber: 'Número de Factura',
        optional: 'Opcional',
        save: 'Guardar',
        clear: 'Limpiar',
        saving: 'Guardando...',
        success: 'Éxito',
        error: 'Error',
        warning: 'Advertencia',
        transactionSaved: '¡Transacción guardada exitosamente!',
        failedSave: 'Error al guardar la transacción',
        selectShopFirst: 'Seleccione una tienda primero',
        noShopSelected: 'No se seleccionó ninguna tienda',
        expenseCategory: 'Categoría de Gasto',
        expenseName: 'Nombre del Gasto',
        unpaidInvoices: 'Facturas Impagas',
        noUnpaidInvoices: 'No se encontraron facturas impagas',
        grandTotal: 'Gran Total',
        alreadyPaid: 'Ya Pagado',
        balanceDue: 'Saldo Pendiente',
        paymentType: 'Tipo de Pago',
        fullPayment: 'Pago Completo',
        partialPayment: 'Pago Parcial',
        paymentDetails: 'Detalles de Pago',
        notes: 'Notas',
        selectReference: 'Seleccionar Referencia',
        expenseDetails: 'Detalles de Gasto',
        general: 'General',
        searchPlaceholder: 'Buscar...',
        noResults: 'No hay resultados',
        select: 'Seleccionar',
        invoice: 'Factura',
        due: 'Pendiente',
        paid: 'Pagado',
        total: 'Total',
        customer: 'Cliente',
        supplier: 'Proveedor',
        employee: 'Empleado',
        name: 'Nombre',
        phone: 'Teléfono',
        email: 'Correo',
        reference: 'Referencia',
        selectInvoice: 'Seleccionar Factura',
        paymentAmount: 'Monto de Pago'
    }
};

// ==============================================
// TOAST NOTIFICATION
// ==============================================
const ToastNotification = ({ toast, setToast, t }) => {
    if (!toast.show) return null;
    return (
        <div className={`costrans_toast-notification ${toast.type}`}>
            <div className="costrans_toast-content">
                <div className="costrans_toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="costrans_toast-text">
                    <div className="costrans_toast-title">{t('success')}</div>
                    <div className="costrans_toast-message">{toast.message}</div>
                </div>
                <button className="costrans_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// TAB BUTTON COMPONENT
// ==============================================
const TabButton = ({ icon, label, isActive, onClick, count }) => (
    <button 
        className={`costrans-tab-btn ${isActive ? 'costrans-active' : ''}`}
        onClick={onClick}
    >
        <FontAwesomeIcon icon={icon} />
        <span>{label}</span>
        {count !== undefined && count > 0 && (
            <span className="costrans-tab-count">{count}</span>
        )}
    </button>
);

// ==============================================
// REFERENCE LIST ITEM
// ==============================================
const ReferenceItem = ({ item, isSelected, onClick, type }) => (
    <div 
        className={`costrans-ref-item ${isSelected ? 'costrans-selected' : ''}`}
        onClick={onClick}
    >
        <div className="costrans-ref-info">
            <span className="costrans-ref-name">{item.name}</span>
            {item.phone && <span className="costrans-ref-detail">{item.phone}</span>}
            {/* {item.email && <span className="costrans-ref-detail">{item.email}</span>} */}
        </div>
        {/* {item.due_amount !== undefined && (
            <div className="costrans-ref-due">
                <span className="costrans-ref-due-label">Due:</span>
                <span className="costrans-ref-due-amount">Rs. {formatCurrency(item.due_amount)}</span>
            </div>
        )} */}
    </div>
);

// ==============================================
// INVOICE LIST ITEM
// ==============================================
const InvoiceItem = ({ invoice, isSelected, onClick }) => (
    <div 
        className={`costrans-invoice-item ${isSelected ? 'costrans-selected' : ''}`}
        onClick={onClick}
    >
        <div className="costrans-invoice-info">
            <span className="costrans-invoice-number">
                {invoice.invoice_number || `INV-${invoice.transaction_id}`}
            </span>
            <span className="costrans-invoice-date">
                {new Date(invoice.transaction_date).toLocaleDateString()}
            </span>
        </div>
        <div className="costrans-invoice-amounts">
            <span className="costrans-invoice-total">
                Rs. {formatCurrency(invoice.amount || 0)}
            </span>
            <span className="costrans-invoice-due">
                Due: Rs. {formatCurrency(invoice.amount_due || invoice.balance || 0)}
            </span>
        </div>
        <div className="costrans-invoice-status">
            <span className={`costrans-status-badge ${invoice.status === 'paid' || invoice.status === 'completed' ? 'costrans-paid' : 'costrans-pending'}`}>
                {invoice.status === 'paid' || invoice.status === 'completed' ? 'Paid' : 'Pending'}
            </span>
        </div>
    </div>
);

// ==============================================
// MAIN COMPONENT
// ==============================================
const AddTransaction = () => {
    const { user, currentShop } = useAuth();

    // ── State ──────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('supplier_payment');
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [searchTerm, setSearchTerm] = useState('');

    // ── Supplier Payment specific state ──────────────────────
    const [selectedReference, setSelectedReference] = useState(null);
    const [supplierUnpaidInvoices, setSupplierUnpaidInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [isFullPayment, setIsFullPayment] = useState(true);

    // ── Form Data ─────────────────────────────────────────────
    const [formData, setFormData] = useState({
        reference_id: '',
        amount: '',
        transaction_date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: 'cash',
        invoice_number: '',
        expense_category: '',
        expense_name: ''
    });

    // ── Language & Theme ──────────────────────────────────────
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });

    // ── Refs ──────────────────────────────────────────────────
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const fetchedRefs = useRef(false);

    // ── Translation helper ──────────────────────────────────
    const t = useCallback((key) => {
        const lang = currentLanguage in translations ? currentLanguage : 'en';
        return translations[lang][key] || key;
    }, [currentLanguage]);

    // ── Toast ──────────────────────────────────────────────────
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    // ── Tab definitions ──────────────────────────────────────
    const tabs = [
        { id: 'customer_due', label: t('customerDue'), icon: faUsers },
        { id: 'customer_payment', label: t('customerPayment'), icon: faUser },
        { id: 'supplier_due', label: t('supplierDue'), icon: faTruck },
        { id: 'supplier_payment', label: t('supplierPayment'), icon: faBuilding },
        { id: 'employee_salary', label: t('salaryPayment'), icon: faUserTie },
        { id: 'expense', label: t('shopExpense'), icon: faReceipt }
    ];

    const paymentMethodOptions = [
        { value: 'cash', label: t('cash') },
        { value: 'bank_transfer', label: t('bankTransfer') },
        { value: 'cheque', label: t('cheque') },
        { value: 'credit_card', label: t('creditCard') },
        { value: 'mobile_payment', label: t('mobilePayment') }
    ];

    // ── Reset form ────────────────────────────────────────────
    const resetForm = useCallback(() => {
        setFormData({
            reference_id: '',
            amount: '',
            transaction_date: new Date().toISOString().split('T')[0],
            description: '',
            payment_method: 'cash',
            invoice_number: '',
            expense_category: '',
            expense_name: ''
        });
        setSelectedReference(null);
        setSelectedInvoice(null);
        setPaymentAmount('');
        setIsFullPayment(true);
        setSupplierUnpaidInvoices([]);
        setSearchTerm('');
    }, []);

    // ── Load customers with balances ─────────────────────────
    const loadCustomers = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(CUSTOMERS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'get_records', 
                    shop_id: currentShop.shop_id 
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                const customersWithBalance = await Promise.all((data.data || []).map(async (c) => {
                    try {
                        // Fetch customer profile with balance
                        const profileRes = await fetch(TRANSACTIONS_API, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'get_customer_profile',
                                shop_id: currentShop.shop_id,
                                customer_id: c.id || c.customerid
                            })
                        });
                        const profileData = await profileRes.json();
                        return {
                            ...c,
                            due_amount: profileData.data?.summary?.total_due || 0
                        };
                    } catch {
                        return { ...c, due_amount: 0 };
                    }
                }));
                setCustomers(customersWithBalance);
            }
        } catch { /* ignore */ }
    }, [currentShop]);

    // ── Load suppliers with balances ─────────────────────────
    const loadSuppliersWithBalances = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'get_all_suppliers', 
                    shop_id: currentShop.shop_id 
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                const suppliersWithBalance = await Promise.all((data.data || []).map(async (s) => {
                    try {
                        const profileRes = await fetch(TRANSACTIONS_API, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'get_supplier_profile',
                                shop_id: currentShop.shop_id,
                                supplier_id: s.id || s.supplierid
                            })
                        });
                        const profileData = await profileRes.json();
                        return {
                            ...s,
                            due_amount: profileData.data?.summary?.total_due || 0
                        };
                    } catch {
                        return { ...s, due_amount: 0 };
                    }
                }));
                setSuppliers(suppliersWithBalance);
            } else {
                // Fallback
                const fallbackRes = await fetch(SUPPLIERS_API, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include'
                });
                const fallbackData = await fallbackRes.json();
                if (fallbackData.status === 'success') {
                    const suppliersWithBalance = await Promise.all((fallbackData.data || []).map(async (s) => {
                        try {
                            const profileRes = await fetch(TRANSACTIONS_API, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    action: 'get_supplier_profile',
                                    shop_id: currentShop.shop_id,
                                    supplier_id: s.id || s.supplierid
                                })
                            });
                            const profileData = await profileRes.json();
                            return {
                                ...s,
                                due_amount: profileData.data?.summary?.total_due || 0
                            };
                        } catch {
                            return { ...s, due_amount: 0 };
                        }
                    }));
                    setSuppliers(suppliersWithBalance);
                }
            }
        } catch (err) {
            console.warn('Failed to load suppliers:', err);
            setSuppliers([]);
        }
    }, [currentShop]);

    // ── Load employees ────────────────────────────────────────
    const loadEmployees = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(EMPLOYEES_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'get_records', 
                    shop_id: currentShop.shop_id 
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                const employeesWithBalance = await Promise.all((data.data || []).map(async (e) => {
                    try {
                        const profileRes = await fetch(TRANSACTIONS_API, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'get_employee_profile',
                                shop_id: currentShop.shop_id,
                                employee_id: e.id || e.employee_id
                            })
                        });
                        const profileData = await profileRes.json();
                        return {
                            ...e,
                            due_amount: profileData.data?.summary?.total_due || 0
                        };
                    } catch {
                        return { ...e, due_amount: 0 };
                    }
                }));
                setEmployees(employeesWithBalance);
            }
        } catch { /* ignore */ }
    }, [currentShop]);

    // ── Load supplier unpaid invoices ────────────────────────
    const loadSupplierUnpaidInvoices = useCallback(async (supplierId) => {
        if (!currentShop || !supplierId) {
            setSupplierUnpaidInvoices([]);
            return;
        }
        try {
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_supplier_unpaid_invoices',
                    shop_id: currentShop.shop_id,
                    supplier_id: supplierId
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setSupplierUnpaidInvoices(data.data || []);
            } else {
                setSupplierUnpaidInvoices([]);
            }
        } catch (err) {
            console.warn('Failed to load supplier unpaid invoices:', err);
            setSupplierUnpaidInvoices([]);
        }
    }, [currentShop]);

    // ── Load all data on mount ───────────────────────────────
    useEffect(() => {
        if (currentShop && !fetchedRefs.current) {
            fetchedRefs.current = true;
            loadCustomers();
            loadSuppliersWithBalances();
            loadEmployees();
        }
    }, [currentShop, loadCustomers, loadSuppliersWithBalances, loadEmployees]);

    // ── Reset form when tab changes ──────────────────────────
    useEffect(() => {
        resetForm();
    }, [activeTab, resetForm]);

    // ── Handle tab change ────────────────────────────────────
    const handleTabChange = useCallback((tabId) => {
        setActiveTab(tabId);
    }, []);

    // ── Handle reference selection ───────────────────────────
    const handleReferenceSelect = useCallback((ref) => {
        setSelectedReference(ref);
        setFormData(prev => ({ ...prev, reference_id: ref.id }));
        
        if (activeTab === 'supplier_payment' && ref.id) {
            loadSupplierUnpaidInvoices(ref.id);
        }
    }, [activeTab, loadSupplierUnpaidInvoices]);

    // ── Handle invoice selection ─────────────────────────────
    const handleInvoiceSelect = useCallback((invoice) => {
        if (!invoice) {
            setSelectedInvoice(null);
            setPaymentAmount('');
            return;
        }
        setSelectedInvoice(invoice);
        const dueAmount = parseFloat(invoice.amount_due || invoice.balance || 0);
        setPaymentAmount(dueAmount.toString());
        setFormData(prev => ({ ...prev, amount: dueAmount.toString() }));
        setIsFullPayment(true);
    }, []);

    // ── Handle payment amount change ──────────────────────────
    const handlePaymentAmountChange = useCallback((value) => {
        setPaymentAmount(value);
        setFormData(prev => ({ ...prev, amount: value }));
        if (selectedInvoice) {
            const dueAmount = parseFloat(selectedInvoice.amount_due || selectedInvoice.balance || 0);
            const enteredAmount = parseFloat(value) || 0;
            setIsFullPayment(enteredAmount >= dueAmount);
        }
    }, [selectedInvoice]);

    // ── Get filtered reference list with balances ────────────
    const getFilteredReferences = useCallback(() => {
        let list = [];
        const search = searchTerm.toLowerCase().trim();

        switch (activeTab) {
            case 'customer_due':
            case 'customer_payment':
                list = customers.map(c => ({
                    id: c.id || c.customerid,
                    name: c.customer_name || c.name,
                    phone: c.phone || c.phonenumber,
                    email: c.email,
                    due_amount: parseFloat(c.due_amount || 0)
                }));
                break;
            case 'supplier_due':
            case 'supplier_payment':
                list = suppliers.map(s => ({
                    id: s.id || s.supplierid,
                    name: s.supplier_name || s.name,
                    phone: s.phone || s.phonenumber,
                    email: s.email,
                    due_amount: parseFloat(s.due_amount || 0)
                }));
                break;
            case 'employee_salary':
                list = employees.map(e => ({
                    id: e.id || e.employee_id,
                    name: e.employee_name || e.name,
                    phone: e.phone || e.phonenumber,
                    email: e.email,
                    due_amount: parseFloat(e.due_amount || 0)
                }));
                break;
            default:
                return [];
        }

        if (search) {
            list = list.filter(item => 
                item.name.toLowerCase().includes(search) ||
                (item.phone && item.phone.includes(search)) ||
                (item.email && item.email.toLowerCase().includes(search))
            );
        }

        // Sort by due amount (highest first)
        return list.sort((a, b) => (b.due_amount || 0) - (a.due_amount || 0));
    }, [activeTab, customers, suppliers, employees, searchTerm]);

    // ── Format date ──────────────────────────────────────────
    const formatDate = useCallback((date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }, []);

    // ── Add transaction ───────────────────────────────────────
    const handleAddTransaction = useCallback(async () => {
        if (isSubmittingRef.current) return;
        if (!currentShop) {
            showToast('error', t('selectShopFirst'));
            return;
        }

        // Validation
        if (activeTab === 'supplier_payment') {
            if (!selectedReference) {
                showToast('error', 'Please select a supplier');
                return;
            }
            if (!selectedInvoice && !formData.amount) {
                showToast('error', 'Please select an invoice or enter a payment amount');
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid payment amount');
                return;
            }
        } else if (activeTab === 'supplier_due') {
            if (!selectedReference) {
                showToast('error', 'Please select a supplier');
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid amount');
                return;
            }
        } else if (activeTab !== 'expense') {
            if (!selectedReference) {
                showToast('error', `Please select a ${activeTab.replace('_', ' ')}`);
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid amount');
                return;
            }
        } else {
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid amount');
                return;
            }
            if (!formData.expense_name) {
                showToast('error', 'Please enter expense name');
                return;
            }
        }

        isSubmittingRef.current = true;
        setLoading(true);

        try {
            const actionMap = {
                customer_due: 'add_due',
                customer_payment: 'add_payment',
                supplier_due: 'add_supplier_due',
                supplier_payment: 'add_supplier_payment',
                employee_salary: 'add_employee_salary',
                expense: 'add_expense'
            };
            const refKeyMap = {
                customer_due: 'customer_id',
                customer_payment: 'customer_id',
                supplier_due: 'supplier_id',
                supplier_payment: 'supplier_id',
                employee_salary: 'employee_id'
            };
            const action = actionMap[activeTab];
            if (!action) throw new Error('Invalid transaction type');

            const body = {
                action,
                shop_id: currentShop.shop_id,
                username: user?.username,
                transaction_date: formData.transaction_date,
                payment_method: formData.payment_method,
                description: formData.description,
                amount: parseFloat(formData.amount)
            };

            if (activeTab === 'supplier_payment') {
                body.supplier_id = parseInt(selectedReference.id);
                if (selectedInvoice) {
                    body.invoice_id = selectedInvoice.transaction_id;
                    body.invoice_number = selectedInvoice.invoice_number;
                    body.is_full_payment = isFullPayment ? 1 : 0;
                }
                if (formData.invoice_number) {
                    body.invoice_number = formData.invoice_number;
                }
            } else if (refKeyMap[activeTab]) {
                body[refKeyMap[activeTab]] = selectedReference.id;
            }

            if (activeTab === 'expense') {
                body.expense_name = formData.expense_name;
                body.expense_category = formData.expense_category || 'General';
            }

            if (activeTab === 'supplier_due') {
                body.invoice_number = formData.invoice_number || `SUP-DUE-${Date.now()}`;
            }

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', t('transactionSaved'));
                resetForm();
                // Refresh balances after transaction
                loadCustomers();
                loadSuppliersWithBalances();
                loadEmployees();
            } else {
                showToast('error', data.message || t('failedSave'));
            }
        } catch (err) {
            showToast('error', err.message || t('failedSave'));
        } finally {
            setLoading(false);
            setTimeout(() => {
                isSubmittingRef.current = false;
            }, 500);
        }
    }, [currentShop, formData, activeTab, user, showToast, resetForm,
        selectedReference, selectedInvoice, isFullPayment, t,
        loadCustomers, loadSuppliersWithBalances, loadEmployees]);

    // ── Cleanup ───────────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ── THEME AUTO-SYNC ──────────────────────────────────────
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
                color = '#10b981';
            }
            document.documentElement.style.setProperty('--costrans-primary', color);
            document.documentElement.style.setProperty('--costrans-primary-dark', color);
            document.documentElement.style.setProperty('--costrans-primary-light', color);
        };

        const savedTheme = localStorage.getItem('ourzest_theme') || '#10b981';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#10b981');
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, []);

    // ── DARK MODE AUTO-SYNC ──────────────────────────────────
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            const container = document.querySelector('.costrans-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('costrans-dark');
                } else {
                    container.classList.remove('costrans-dark');
                }
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = () => {
            syncDarkMode();
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    // ── LANGUAGE AUTO-SYNC ────────────────────────────────────
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                }
            }
        };

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
        };
    }, [currentLanguage]);

    // ── RTL SYNC ──────────────────────────────────────────────
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    useEffect(() => {
        const container = document.querySelector('.costrans-container');
        if (container) {
            if (isRTL) {
                container.classList.add('costrans-rtl');
                container.classList.remove('costrans-ltr');
            } else {
                container.classList.add('costrans-ltr');
                container.classList.remove('costrans-rtl');
            }
        }
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [isRTL]);

    // ── Get reference label ──────────────────────────────────
    const getReferenceLabel = useCallback(() => {
        switch (activeTab) {
            case 'customer_due':
            case 'customer_payment':
                return t('customer');
            case 'supplier_due':
            case 'supplier_payment':
                return t('supplier');
            case 'employee_salary':
                return t('employee');
            default:
                return t('reference');
        }
    }, [activeTab, t]);

    // ── Render ─────────────────────────────────────────────────
    const filteredReferences = getFilteredReferences();
    const isSupplierPayment = activeTab === 'supplier_payment';
    const isSupplierDue = activeTab === 'supplier_due';
    const isExpense = activeTab === 'expense';
    const isCustomerTab = activeTab === 'customer_due' || activeTab === 'customer_payment';
    const isEmployeeTab = activeTab === 'employee_salary';

    return (
        <div className={`costrans-container ${isRTL ? 'costrans-rtl' : 'costrans-ltr'} ${darkMode ? 'costrans-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} />

            {/* Header */}
            <div className="costrans-sticky-header">
                <div className="costrans-header-bg-animation">
                    <div className="costrans-gradient-orb costrans-orb-1"></div>
                    <div className="costrans-gradient-orb costrans-orb-2"></div>
                    <div className="costrans-gradient-orb costrans-orb-3"></div>
                    <div className="costrans-gradient-orb costrans-orb-4"></div>
                    <div className="costrans-gradient-orb costrans-orb-5"></div>
                </div>
                <div className="costrans-header-content">
                    <div className="costrans-header-left">
                        <div className="costrans-header-title">
                            <h1>
                               
                                {t('addTransaction')}
                            </h1>
                            <p>{t('addNewTransaction')}</p>
                        </div>
                    </div>
                    <div className="costrans-header-right">
                        {!currentShop && (
                            <div className="costrans-no-shop-warning">
                                <FontAwesomeIcon icon={faStore} />
                                <span>{t('noShopSelected')}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Two Column Layout */}
            <div className="costrans-two-column-layout">
                {/* Left Column */}
                <div className="costrans-left-column">
                    {/* Upper Section - Tabs */}
                    <div className="costrans-tabs-section">
                        {tabs.map(tab => (
                            <TabButton
                                key={tab.id}
                                icon={tab.icon}
                                label={tab.label}
                                isActive={activeTab === tab.id}
                                onClick={() => handleTabChange(tab.id)}
                            />
                        ))}
                    </div>

                    {/* Lower Section - Reference List */}
                    <div className="costrans-ref-list-section">
                        <div className="costrans-ref-search">
                            <FontAwesomeIcon icon={faSearch} className="costrans-search-icon" />
                            <input
                                type="text"
                                placeholder={t('searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="costrans-search-input"
                            />
                        </div>
                        <div className="costrans-ref-list">
                            {filteredReferences.length === 0 ? (
                                <div className="costrans-no-results">
                                    <FontAwesomeIcon icon={faList} />
                                    <span>{t('noResults')}</span>
                                </div>
                            ) : (
                                filteredReferences.map(ref => (
                                    <ReferenceItem
                                        key={ref.id}
                                        item={ref}
                                        isSelected={selectedReference?.id === ref.id}
                                        onClick={() => handleReferenceSelect(ref)}
                                        type={activeTab}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="costrans-right-column">
                    {/* Upper Section - Invoices / Details */}
                    <div className="costrans-invoice-section">
                        {isSupplierPayment && selectedReference ? (
                            <>
                                <div className="costrans-section-header">
                                    <FontAwesomeIcon icon={faFileInvoice} />
                                    <span>{t('unpaidInvoices')}</span>
                                    <span className="costrans-badge">{supplierUnpaidInvoices.length}</span>
                                </div>
                                <div className="costrans-invoice-list">
                                    {supplierUnpaidInvoices.length === 0 ? (
                                        <div className="costrans-no-data">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{t('noUnpaidInvoices')}</span>
                                        </div>
                                    ) : (
                                        supplierUnpaidInvoices.map(inv => (
                                            <InvoiceItem
                                                key={inv.transaction_id}
                                                invoice={inv}
                                                isSelected={selectedInvoice?.transaction_id === inv.transaction_id}
                                                onClick={() => handleInvoiceSelect(inv)}
                                            />
                                        ))
                                    )}
                                </div>
                            </>
                        ) : isCustomerTab || isSupplierDue || isEmployeeTab ? (
                            <div className="costrans-selected-info">
                                {selectedReference ? (
                                    <div className="costrans-info-card">
                                        <div className="costrans-info-header">
                                            <FontAwesomeIcon icon={faUser} className="costrans-info-icon" />
                                            <span className="costrans-info-name">{selectedReference.name}</span>
                                        </div>
                                        {selectedReference.phone && (
                                            <div className="costrans-info-row">
                                                <span className="costrans-info-label">Phone:</span>
                                                <span>{selectedReference.phone}</span>
                                            </div>
                                        )}
                                        {selectedReference.email && (
                                            <div className="costrans-info-row">
                                                <span className="costrans-info-label">Email:</span>
                                                <span>{selectedReference.email}</span>
                                            </div>
                                        )}
                                        {selectedReference.due_amount > 0 && (
                                            <div className="costrans-info-row costrans-due-row">
                                                <span className="costrans-info-label">Due Amount:</span>
                                                <span className="costrans-due-amount">Rs. {formatCurrency(selectedReference.due_amount)}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="costrans-select-prompt">
                                        <FontAwesomeIcon icon={faArrowLeft} className="costrans-prompt-icon" />
                                        <span>Select a {getReferenceLabel()} from the left panel</span>
                                    </div>
                                )}
                            </div>
                        ) : isExpense ? (
                            <div className="costrans-expense-details">
                                <div className="costrans-section-header">
                                    <FontAwesomeIcon icon={faReceipt} />
                                    <span>{t('expenseDetails')}</span>
                                </div>
                                <div className="costrans-expense-fields">
                                    <div className="costrans-field-group">
                                        <input
                                            type="text"
                                            placeholder=" "
                                            value={formData.expense_category}
                                            onChange={e => setFormData(prev => ({ ...prev, expense_category: e.target.value }))}
                                            disabled={loading}
                                            className="costrans-input"
                                        />
                                        <label>{t('expenseCategory')}</label>
                                    </div>
                                    <div className="costrans-field-group">
                                        <input
                                            type="text"
                                            placeholder=" "
                                            value={formData.expense_name}
                                            onChange={e => setFormData(prev => ({ ...prev, expense_name: e.target.value }))}
                                            disabled={loading}
                                            className="costrans-input"
                                        />
                                        <label>{t('expenseName')}</label>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Lower Section - Payment Details */}
                    <div className="costrans-payment-section">
                        <div className="costrans-section-header">
                            <FontAwesomeIcon icon={faWallet} />
                            <span>{t('paymentDetails')}</span>
                        </div>

                        <div className="costrans-payment-grid">
                            <div className="costrans-field-group costrans-amount-field">
                                {/* <span className="costrans-currency-label">Rs.</span> */}
                                <input
                                    type="number"
                                    // step="0"
                                    placeholder=" "
                                    className="costrans-amount-input"
                                    value={paymentAmount || formData.amount}
                                    onChange={e => handlePaymentAmountChange(e.target.value)}
                                    disabled={loading}
                                />
                                <label>{t('Rs.')}</label>
                            </div>

                            <div className="costrans-field-group">
                                <input
                                    type="date"
                                    placeholder=" "
                                    value={formData.transaction_date}
                                    onChange={e => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                                    disabled={loading}
                                />
                                <label><FontAwesomeIcon icon={faCalendarAlt} />{t('date')}</label>
                            </div>

                            <div className="costrans-field-group costrans-select-group">
                                <select
                                    value={formData.payment_method}
                                    onChange={e => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                                    disabled={loading}
                                    className="costrans-select"
                                >
                                    {paymentMethodOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <label><FontAwesomeIcon icon={faCreditCard} />{t('paymentMethod')}</label>
                                <FontAwesomeIcon icon={faChevronDown} className="costrans-select-arrow" />
                            </div>

                            <div className="costrans-field-group">
                                <input
                                    type="text"
                                    placeholder=" "
                                    value={formData.invoice_number}
                                    onChange={e => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                    disabled={loading}
                                />
                                <label><FontAwesomeIcon icon={faFileInvoice} />{t('invoiceNumber')} <span className="costrans-optional">({t('optional')})</span></label>
                            </div>
                        </div>

                        {/* Payment Preview for Supplier Payment */}
                        {isSupplierPayment && selectedInvoice && (
                            <div className="costrans-payment-preview">
                                <div className="costrans-preview-row">
                                    <span>{t('grandTotal')}:</span>
                                    <span>Rs. {formatCurrency(selectedInvoice.amount || 0)}</span>
                                </div>
                                <div className="costrans-preview-row">
                                    <span>{t('alreadyPaid')}:</span>
                                    <span>Rs. {formatCurrency(selectedInvoice.amount_paid || selectedInvoice.paid_amount || 0)}</span>
                                </div>
                                <div className="costrans-preview-row costrans-highlight">
                                    <span>{t('balanceDue')}:</span>
                                    <span>Rs. {formatCurrency(selectedInvoice.amount_due || selectedInvoice.balance || 0)}</span>
                                </div>
                                <div className="costrans-preview-row">
                                    <span>{t('paymentType')}:</span>
                                    <span className={isFullPayment ? 'costrans-full' : 'costrans-partial'}>
                                        {isFullPayment ? t('fullPayment') : t('partialPayment')}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        <div className="costrans-field-group costrans-textarea-group">
                            <textarea
                                rows="2"
                                placeholder=" "
                                value={formData.description}
                                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                disabled={loading}
                            />
                            <label><FontAwesomeIcon icon={faStickyNote} />{t('notes')}</label>
                        </div>

                        {/* Action Buttons */}
                        <div className="costrans-form-actions">
                            <button
                                type="button"
                                className="costrans-btn costrans-btn-secondary"
                                onClick={resetForm}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{t('clear')}</span>
                            </button>

                            <button
                                type="submit"
                                className="costrans-btn costrans-btn-primary"
                                onClick={handleAddTransaction}
                                disabled={loading || !currentShop}
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <span>{t('saving')}</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        <span>{t('save')}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddTransaction;